import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, TextField } from "@mui/material";

export interface Place {
  address: string;
  placeId: string;
  name?: string;
  lat?: number;
  lng?: number;
  formattedAddress?: string;
  addressComponents?: google.maps.GeocoderAddressComponent[];
  rawData?: google.maps.places.PlaceResult;
}

interface GooglePlacesAutocompleteProps {
  apiKey: string;
  onPlaceSelected: (place: Place) => void;
  placeholder?: string;
  initialValue?: string;
  className?: string;
  inputClassName?: string;
  suggestionsClassName?: string;
  suggestionsItemClassName?: string;
  options?: google.maps.places.AutocompleteOptions;
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  apiKey,
  onPlaceSelected,
  placeholder = "Search for the address",
  initialValue = "",
  className = "googleAutoCompleteField",
  inputClassName = "",
  suggestionsClassName = "",
  suggestionsItemClassName = "",
  options = {},
}) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);

  useEffect(() => {
    if (window.google && window.google.maps) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setScriptLoaded(true);
    };
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, [apiKey]);

  useEffect(() => {
    if (scriptLoaded && window.google) {
      autocompleteService.current =
        new google.maps.places.AutocompleteService();

      const dummyDiv = document.createElement("div");
      dummyDiv.style.display = "none";
      document.body.appendChild(dummyDiv);
      placesService.current = new google.maps.places.PlacesService(dummyDiv);
    }
  }, [scriptLoaded]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.length > 0 && autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        {
          input: value,
          ...options,
        },
        (predictions, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        },
      );
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (placeId: string) => {
    if (placesService.current) {
      placesService.current.getDetails(
        {
          placeId,
          fields: [
            "address_components",
            "formatted_address",
            "geometry",
            "name",
          ],
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            setInputValue(place.formatted_address || "");
            setSuggestions([]);
            setShowSuggestions(false);

            const selectedPlace: Place = {
              address: place.formatted_address || "",
              placeId: place.place_id || "",
              name: place.name,
              lat: place.geometry?.location?.lat(),
              lng: place.geometry?.location?.lng(),
              formattedAddress: place.formatted_address,
              addressComponents: place.address_components,
              rawData: place,
            };

            onPlaceSelected(selectedPlace);
          }
        },
      );
    }
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  return (
    <Box className={`google-places-autocomplete ${className}`}>
      <Autocomplete
        freeSolo
        disableClearable
        options={suggestions}
        getOptionLabel={(option) =>
          (typeof option === "string" ? option : option.description) || ""
        }
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          if (event) {
            handleInputChange({
              target: { value: newInputValue },
            } as React.ChangeEvent<HTMLInputElement>);
          }
        }}
        onChange={(event, newValue) => {
          if (newValue && typeof newValue !== "string" && newValue.place_id) {
            handleSuggestionClick(newValue.place_id);
          }
        }}
        disabled={!scriptLoaded}
        filterOptions={(x) => x}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            placeholder={placeholder}
            className={`google-places-autocomplete-input ${inputClassName}`}
            inputRef={inputRef}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.place_id}
            style={{ cursor: "pointer", borderBottom: "1px solid gray" }}
            className={`google-places-autocomplete-suggestion-item ${suggestionsItemClassName}`}
          >
            {option.description}
          </li>
        )}
        open={showSuggestions && suggestions.length > 0}
        classes={{
          popper: `google-places-autocomplete-suggestions ${suggestionsClassName}`,
        }}
      />
    </Box>
  );
};

export default GooglePlacesAutocomplete;
